/*
 * Synthesized key sounds for the keyboard visuals. Nothing is downloaded:
 * every keystroke is built from filtered noise and a short low thump with the
 * Web Audio API, so it only ever runs in the browser after a click.
 */

export interface StrokeOptions {
    intensity?: number
    pitch?: number
    space?: boolean
}

type AudioContextConstructor = new () => AudioContext

const noiseBuffers = new WeakMap<BaseAudioContext, AudioBuffer>()

let sharedContext: AudioContext | null = null
let sharedOutput: AudioNode | null = null

export const createAudioContext = (): AudioContext | null => {
    if (typeof window === 'undefined') return null

    const legacy = (window as unknown as { webkitAudioContext?: AudioContextConstructor }).webkitAudioContext
    const Constructor: AudioContextConstructor | undefined = window.AudioContext ?? legacy

    if (!Constructor) return null

    try {
        return new Constructor()
    }
    catch {
        return null
    }
}

export const createOutput = (context: AudioContext, volume = 0.7): AudioNode => {
    const gain = context.createGain()
    const compressor = context.createDynamicsCompressor()

    gain.gain.value = volume
    compressor.threshold.value = -18
    compressor.ratio.value = 3

    gain.connect(compressor)
    compressor.connect(context.destination)

    return gain
}

const getNoise = (context: BaseAudioContext): AudioBuffer => {
    const cached = noiseBuffers.get(context)

    if (cached) return cached

    const length = Math.floor(context.sampleRate * 0.3)
    const buffer = context.createBuffer(1, length, context.sampleRate)
    const data = buffer.getChannelData(0)

    for (let index = 0; index < length; index++) {
        data[index] = Math.random() * 2 - 1
    }

    noiseBuffers.set(context, buffer)

    return buffer
}

export const scheduleKeystroke = (
    context: AudioContext,
    destination: AudioNode,
    time: number,
    { intensity = 1, pitch = 1, space = false }: StrokeOptions = {}
): void => {
    const noise = getNoise(context)
    const level = Math.max(0.05, Math.min(1.2, intensity))

    const click = context.createBufferSource()
    const clickFilter = context.createBiquadFilter()
    const clickGain = context.createGain()

    click.buffer = noise
    clickFilter.type = 'bandpass'
    clickFilter.frequency.value = (space ? 950 : 1900) * pitch
    clickFilter.Q.value = 0.9
    clickGain.gain.setValueAtTime(0.0001, time)
    clickGain.gain.exponentialRampToValueAtTime(0.55 * level, time + 0.002)
    clickGain.gain.exponentialRampToValueAtTime(0.0001, time + (space ? 0.075 : 0.045))

    click.connect(clickFilter)
    clickFilter.connect(clickGain)
    clickGain.connect(destination)
    click.start(time, Math.random() * 0.15, 0.1)

    const body = context.createOscillator()
    const bodyGain = context.createGain()

    body.type = 'sine'
    body.frequency.setValueAtTime((space ? 125 : 195) * pitch, time)
    body.frequency.exponentialRampToValueAtTime((space ? 70 : 110) * pitch, time + 0.05)
    bodyGain.gain.setValueAtTime(0.0001, time)
    bodyGain.gain.exponentialRampToValueAtTime(0.32 * level, time + 0.003)
    bodyGain.gain.exponentialRampToValueAtTime(0.0001, time + (space ? 0.12 : 0.07))

    body.connect(bodyGain)
    bodyGain.connect(destination)
    body.start(time)
    body.stop(time + 0.16)

    const release = time + (space ? 0.12 : 0.085) + Math.random() * 0.03
    const up = context.createBufferSource()
    const upFilter = context.createBiquadFilter()
    const upGain = context.createGain()

    up.buffer = noise
    upFilter.type = 'highpass'
    upFilter.frequency.value = 2600 * pitch
    upGain.gain.setValueAtTime(0.0001, release)
    upGain.gain.exponentialRampToValueAtTime(0.14 * level, release + 0.002)
    upGain.gain.exponentialRampToValueAtTime(0.0001, release + 0.03)

    up.connect(upFilter)
    upFilter.connect(upGain)
    upGain.connect(destination)
    up.start(release, Math.random() * 0.15, 0.05)
}

export const playKeySound = (options?: StrokeOptions): void => {
    if (!sharedContext) {
        sharedContext = createAudioContext()

        if (!sharedContext) return

        sharedOutput = createOutput(sharedContext, 0.45)
    }

    if (sharedContext.state === 'suspended') void sharedContext.resume()

    if (sharedOutput) scheduleKeystroke(sharedContext, sharedOutput, sharedContext.currentTime + 0.005, options)
}
